import React, { useState } from 'react';
import { 
  MessageSquare, 
  Send, 
  CheckCircle, 
  Star, 
  Monitor, 
  Users, 
  BarChart, 
  GraduationCap, 
  Handshake, 
  Clock, 
  Check 
} from 'lucide-react';
import CollegeHeader from './CollegeHeader';
import CollegeHomeSideBar from './CollegeHomeSideBar';

// --- Design tokens ------------------------------------------------------------
const T = {
  border: '1px solid var(--border-subtle, #E2E8F0)',
  radius: { sm: '8px', md: '10px', lg: '14px', xl: '18px', full: '999px' },
  shadow: { xs: '0 1px 3px rgba(0,0,0,0.06)', sm: '0 2px 8px rgba(0,0,0,0.08)' },
};

const RATING_LABELS = {
  1: { label: 'Poor',      color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
  2: { label: 'Fair',      color: '#EA580C', bg: '#FFF7ED', border: '#FED7AA' },
  3: { label: 'Good',      color: '#CA8A04', bg: '#FEF9C3', border: '#FDE047' },
  4: { label: 'Very Good', color: '#16A34A', bg: '#DCFCE7', border: '#BBF7D0' },
  5: { label: 'Excellent', color: 'var(--primary-blue, #4F46E5)', bg: '#EFF6FF', border: '#BFDBFE' },
};

const CATEGORIES = [
  { key: 'platform',  label: 'Platform Experience', icon: <Monitor size={16} /> },
  { key: 'support',   label: 'Support Quality',      icon: <Handshake size={16} /> },
  { key: 'analytics', label: 'Analytics & Reports',  icon: <BarChart size={16} /> },
  { key: 'students',  label: 'Student Management',   icon: <GraduationCap size={16} /> },
  { key: 'other',     label: 'Other',                icon: <MessageSquare size={16} /> },
];

// --- Main component -----------------------------------------------------------
const CollegeFeedbackInbox = () => {
  const [rating,    setRating]    = useState(0);
  const [hover,     setHover]     = useState(0);
  const [feedback,  setFeedback]  = useState('');
  const [category,  setCategory]  = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting,setSubmitting]= useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Handle feedback submission logic (e.g., API call)
    console.log('Rating:', rating);
    console.log('Feedback:', feedback);
    console.log('Category:', category);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 800);
  };

  const handleReset = () => {
    setRating(0);
    setHover(0);
    setFeedback('');
    setCategory('');
    setSubmitted(false);
  };

  const activeRating = hover || rating;
  const ratingMeta   = RATING_LABELS[activeRating];
  const charLimit    = 500;

  return (
    <div className="college-main-layout" style={{ background: '#F8FAFC' }}>
      <CollegeHeader />

      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        <main className="college-main-content" style={{ padding: '32px 28px' }}>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>

            {/* Page header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
              <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'rgba(79, 70, 229,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <MessageSquare size={22} style={{ color: 'var(--primary-blue, #4F46E5)' }} />
              </div>
              <div>
                <h1 style={{ fontSize: '1.625rem', fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em', margin: 0 }}>
                  Feedback Inbox
                </h1>
                <p style={{ color: '#64748B', fontSize: '0.875rem', margin: '3px 0 0' }}>
                  Share your experience to help us improve the platform
                </p>
              </div>
            </div>

            {/* Success state */}
            {submitted ? (
              <div style={{
                background: '#FFFFFF', border: '1px solid #BBF7D0', borderRadius: T.radius.xl,
                padding: '64px 40px', textAlign: 'center', boxShadow: T.shadow.sm,
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, #16A34A, #059669)', borderRadius: `${T.radius.xl} ${T.radius.xl} 0 0` }} />
                <div style={{
                  width: 72, height: 72, borderRadius: '50%',
                  background: '#DCFCE7', border: '3px solid #BBF7D0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 20px',
                }}>
                  <CheckCircle size={36} style={{ color: '#16A34A' }} />
                </div>
                <h2 style={{ fontSize: '1.375rem', fontWeight: 800, color: '#0F172A', margin: '0 0 10px', letterSpacing: '-0.01em' }}>
                  Thank you for your feedback!
                </h2>
                <p style={{ color: '#64748B', fontSize: '0.9375rem', margin: '0 0 28px', lineHeight: 1.6 }}>
                  Your response has been recorded. We'll use it to make the platform better for everyone.
                </p>
                {rating > 0 && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '10px 20px', borderRadius: T.radius.full,
                    background: RATING_LABELS[rating].bg, border: `1px solid ${RATING_LABELS[rating].border}`,
                    marginBottom: 28,
                  }}>
                    <span style={{ fontSize: '1.1rem' }}>{'★'.repeat(rating)}{'☆'.repeat(5 - rating)}</span>
                    <span style={{ fontSize: '0.875rem', fontWeight: 700, color: RATING_LABELS[rating].color }}>
                      {RATING_LABELS[rating].label}
                    </span>
                  </div>
                )}
                <div>
                  <button
                    onClick={handleReset}
                    style={{
                      padding: '10px 24px', borderRadius: T.radius.md,
                      background: 'var(--primary-blue, #4F46E5)', color: '#FFFFFF', border: 'none',
                      fontSize: '0.875rem', fontWeight: 700, cursor: 'pointer',
                      transition: 'opacity 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    Submit Another Response
                  </button>
                </div>
              </div>
            ) : (
              /* Two-panel layout */
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 20, alignItems: 'start' }}>

                {/* Left panel - info */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                  {/* Why it matters card */}
                  <div style={{
                    background: 'linear-gradient(135deg, #1E3A8A, var(--primary-blue, #4F46E5))',
                    borderRadius: T.radius.xl, padding: '28px 24px', color: '#FFFFFF',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                    <div style={{ position: 'absolute', bottom: -30, left: -10, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
                    <MessageSquare size={28} style={{ color: 'rgba(255,255,255,0.8)', marginBottom: 14 }} />
                    <h3 style={{ fontSize: '1.0625rem', fontWeight: 800, margin: '0 0 10px', letterSpacing: '-0.01em' }}>
                      Your voice matters
                    </h3>
                    <p style={{ fontSize: '0.875rem', lineHeight: 1.7, margin: 0, color: 'rgba(255,255,255,0.8)' }}>
                      Every piece of feedback directly shapes the platform. Our team reviews every submission within 48 hours.
                    </p>
                  </div>

                  {/* Category quick-pick */}
                  <div style={{
                    background: '#FFFFFF', border: T.border, borderRadius: T.radius.xl,
                    padding: '20px', boxShadow: T.shadow.xs, position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #7C3AED, var(--primary-blue, #4F46E5))', borderRadius: `${T.radius.xl} ${T.radius.xl} 0 0` }} />
                    <p style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '4px 0 14px' }}>
                      Topic (click to select)
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {CATEGORIES.map(cat => (
                        <button
                          key={cat.key}
                          onClick={() => setCategory(cat.key)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 14px', borderRadius: T.radius.md,
                            border: category === cat.key ? '1px solid #BFDBFE' : T.border,
                            background: category === cat.key ? '#EFF6FF' : '#F8FAFC',
                            cursor: 'pointer', textAlign: 'left',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => { if (category !== cat.key) e.currentTarget.style.background = '#F1F5F9'; }}
                          onMouseLeave={e => { if (category !== cat.key) e.currentTarget.style.background = '#F8FAFC'; }}
                        >
                          <span style={{ fontSize: '1rem' }}>{cat.icon}</span>
                          <span style={{
                            fontSize: '0.8125rem', fontWeight: category === cat.key ? 700 : 500,
                            color: category === cat.key ? 'var(--secondary-blue, #1E3A8A)' : '#374151',
                          }}>
                            {cat.label}
                          </span>
                          {category === cat.key && (
                            <div style={{ marginLeft: 'auto', width: 8, height: 8, borderRadius: '50%', background: 'var(--primary-blue, #4F46E5)' }} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Response time note */}
                  <div style={{
                    background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: T.radius.lg,
                    padding: '14px 16px',
                  }}>
                    <p style={{ fontSize: '0.8rem', color: '#92400E', margin: 0, lineHeight: 1.6 }}>
                      <strong>Response time:</strong> Our team typically responds within 1-2 business days for critical feedback.
                    </p>
                  </div>
                </div>

                {/* Right panel - form */}
                <div style={{
                  background: '#FFFFFF', border: T.border, borderRadius: T.radius.xl,
                  boxShadow: T.shadow.sm, overflow: 'hidden',
                }}>

                  <form onSubmit={handleSubmit} style={{ padding: '28px 28px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>

                    {/* Star rating */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
                        Overall Rating <span style={{ color: '#DC2626' }}>*</span>
                      </label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setRating(star)}
                            onMouseEnter={() => setHover(star)}
                            onMouseLeave={() => setHover(0)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                              fontSize: '2rem', lineHeight: 1,
                              filter: star <= activeRating ? 'none' : 'grayscale(1) opacity(0.3)',
                              transform: star <= activeRating ? 'scale(1.15)' : 'scale(1)',
                              transition: 'transform 0.15s, filter 0.15s',
                              color: '#F59E0B',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Star fill={star <= activeRating ? "currentColor" : "none"} size={32} />
                          </button>
                        ))}
                        {ratingMeta && (
                          <span style={{
                            marginLeft: 8, padding: '4px 12px', borderRadius: T.radius.full,
                            background: ratingMeta.bg, border: `1px solid ${ratingMeta.border}`,
                            fontSize: '0.8rem', fontWeight: 700, color: ratingMeta.color,
                          }}>
                            {ratingMeta.label}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Category select (synced with left panel) */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                        Category
                      </label>
                      <select
                        value={category}
                        onChange={e => setCategory(e.target.value)}
                        style={{
                          width: '100%', padding: '9px 12px', border: T.border, borderRadius: T.radius.md,
                          fontSize: '0.875rem', color: '#0F172A', background: '#FFFFFF',
                          outline: 'none', appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer',
                          boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s',
                        }}
                        onFocus={e => { e.target.style.borderColor = 'var(--primary-blue, #4F46E5)'; e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229,0.1)'; }}
                        onBlur={e  => { e.target.style.borderColor = 'var(--border-subtle,#E2E8F0)'; e.target.style.boxShadow = 'none'; }}
                      >
                        <option value="">Select a category</option>
                        {CATEGORIES.map(c => (
                          <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Feedback textarea */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                        Your Feedback <span style={{ color: '#DC2626' }}>*</span>
                      </label>
                      <textarea
                        rows={6}
                        placeholder="What can we do to improve your experience?"
                        value={feedback}
                        onChange={e => setFeedback(e.target.value.slice(0, charLimit))}
                        style={{
                          width: '100%', padding: '12px 14px',
                          border: T.border, borderRadius: T.radius.md,
                          fontSize: '0.875rem', color: '#0F172A',
                          background: '#FFFFFF', outline: 'none', resize: 'vertical',
                          fontFamily: 'inherit', lineHeight: 1.6,
                          boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s',
                        }}
                        onFocus={e => { e.target.style.borderColor = 'var(--primary-blue, #4F46E5)'; e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229,0.1)'; }}
                        onBlur={e  => { e.target.style.borderColor = 'var(--border-subtle,#E2E8F0)'; e.target.style.boxShadow = 'none'; }}
                      />
                      <p style={{ fontSize: '0.75rem', color: feedback.length >= charLimit - 20 ? '#DC2626' : '#94A3B8', margin: '6px 0 0', textAlign: 'right' }}>
                        {feedback.length}/{charLimit}
                      </p>
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={!rating || !feedback.trim() || submitting}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        width: '100%', padding: '12px 24px',
                        borderRadius: T.radius.md, border: 'none',
                        background: !rating || !feedback.trim() ? '#CBD5E1' : 'linear-gradient(135deg, var(--primary-blue, #4F46E5), #4F46E5)',
                        color: '#FFFFFF', fontWeight: 800, fontSize: '0.9375rem',
                        cursor: !rating || !feedback.trim() || submitting ? 'not-allowed' : 'pointer',
                        boxShadow: !rating || !feedback.trim() ? 'none' : '0 4px 14px rgba(79, 70, 229,0.3)',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => { if (rating && feedback.trim() && !submitting) e.currentTarget.style.opacity = '0.9'; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                    >
                      {submitting ? (
                        <>
                          <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.7s linear infinite' }} />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          Submit Feedback
                        </>
                      )}
                    </button>

                    {(!rating || !feedback.trim()) && (
                      <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#94A3B8', margin: '-16px 0 0' }}>
                        {!rating ? 'Please select a star rating' : 'Please enter your feedback'}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
};

export default CollegeFeedbackInbox;


